import type { HelpCenterFolder, HelpCenterSiteTreeNode, SiteNavigationSplit } from './types';

export type SiteNavigationMeasurements = {
	availableWidth: number | null;
	folderWidths: Readonly<Record<string, number>>;
	gapWidth: number;
	moreWidth: number;
};

// Allow 1px of sub-pixel rounding so the split does not flap.
const COMPARISON_TOLERANCE = 1;

const getRowWidth = ({
	folderWidths,
	folders,
	gapWidth,
	moreWidth,
	showMore,
}: Omit<SiteNavigationMeasurements, 'availableWidth'> & {
	folders: HelpCenterFolder[];
	showMore: boolean;
}): number => {
	const visibleItemCount = folders.length + (showMore ? 1 : 0);
	const visibleGapCount = Math.max(visibleItemCount - 1, 0);
	// An unmeasured folder is unfittable, so it stays in More.
	const promotedFolderWidth = folders.reduce(
		(total, { id }) => total + (folderWidths[id] ?? Number.MAX_SAFE_INTEGER),
		0,
	);

	return promotedFolderWidth + (showMore ? moreWidth : 0) + visibleGapCount * gapWidth;
};

export const splitSiteNavigation = (
	items: readonly HelpCenterSiteTreeNode[],
	measurements: SiteNavigationMeasurements,
): SiteNavigationSplit => {
	const folders = items.filter(
		(item): item is HelpCenterFolder => item.__typename === 'HelpCenterFolder',
	);
	const conservativeSplit = { promotedFolders: [], overflowItems: [...items] };

	if (measurements.availableWidth === null || folders.length === 0) {
		return conservativeSplit;
	}
	const availableWidth = measurements.availableWidth;

	const fits = (candidateFolders: HelpCenterFolder[], showMore: boolean): boolean =>
		getRowWidth({ ...measurements, folders: candidateFolders, showMore }) <=
		availableWidth + COMPARISON_TOLERANCE;

	// "More" is optional when every item is a folder and it fits.
	const hasTopLevelPages = items.some((item) => item.__typename === 'HelpCenterPage');
	if (!hasTopLevelPages && fits(folders, false)) {
		return { promotedFolders: folders, overflowItems: [] };
	}

	// Reserve "More" and promote folders left to right while they still fit beside it.
	const promotedFolders: HelpCenterFolder[] = [];
	for (const folder of folders) {
		const candidateFolders = [...promotedFolders, folder];
		if (!fits(candidateFolders, true)) {
			break;
		}
		promotedFolders.push(folder);
	}

	const promotedIds = new Set(promotedFolders.map(({ id }) => id));
	return {
		promotedFolders,
		overflowItems: items.filter(
			(item) => item.__typename === 'HelpCenterPage' || !promotedIds.has(item.id),
		),
	};
};
